/**
 * Firestore Document Sanitization Utility
 *
 * This utility removes base64 image data from documents before storing in Firestore
 * to prevent hitting the 1MB document size limit.
 */

const BASE64_PREFIXES = ['data:image/', '/9j/', 'iVBOR', 'UklGR'];
const TRANSIENT_JSON_FIELDS = new Set(['aiSuggestionPromises', 'aiStreamingUpdates$']);

function isBase64ImageString(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();
  return BASE64_PREFIXES.some((prefix) => trimmed.startsWith(prefix)) || trimmed.length > 5000;
}

/**
 * Sanitizes data for Firestore by removing base64 image data and keeping only URLs
 * This prevents hitting the 1MB document size limit
 */
export function sanitizeDataForFirestore(data: any): any {
  if (!data) return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeDataForFirestore(item));
  }

  // Handle objects
  if (typeof data === 'object' && data !== null) {
    const sanitized = { ...data };

    // Check if this is a gallery block with images
    if (sanitized.type === 'gallery' && Array.isArray(sanitized.arr)) {
      sanitized.arr = sanitized.arr.map((image) => {
        if (typeof image === 'object' && image !== null) {
          const sanitizedImage = { ...image };

          const transientResolvedUrl = sanitizedImage.resolvedUrl || sanitizedImage.source_url;
          delete sanitizedImage.resolvedUrl;
          delete sanitizedImage.resolvedUrlExpiresAt;
          if (sanitizedImage.provider === 'r2' && sanitizedImage.visibility === 'private') {
            if (sanitizedImage.image === transientResolvedUrl) delete sanitizedImage.image;
            if (sanitizedImage.thumbnail === transientResolvedUrl) delete sanitizedImage.thumbnail;
            delete sanitizedImage.source_url;
            delete sanitizedImage.sourceUrl;
          }

          const hasSourceUrl = typeof sanitizedImage.source_url === 'string' && sanitizedImage.source_url.length > 0;
          const imageValue = sanitizedImage.image;
          const isCurrentlyUploading = Boolean(sanitizedImage.isUploading || sanitizedImage.pendingUpload);
          const containsBase64Image = isBase64ImageString(imageValue);

          if (hasSourceUrl && containsBase64Image) {
            // Replace base64 with the confirmed URL
            sanitizedImage.image = sanitizedImage.source_url;
          } else if (containsBase64Image && !isCurrentlyUploading) {
            // Remove base64 data when there's no confirmed URL and the upload is not in progress
            delete sanitizedImage.image;
          }

          // Remove AI suggestion promises as they can't be serialized
          delete sanitizedImage.aiSuggestionPromises;
          delete sanitizedImage.aiStreamingUpdates$;

          // Remove AI suggestions to reduce data size
          delete sanitizedImage.aiSuggestions;
          delete sanitizedImage.localDataUrl;

          // Remove any other large temporary fields
          delete sanitizedImage.pendingUpload;
          delete sanitizedImage.uploadProgress;

          return sanitizedImage;
        }
        return image;
      });
    }

    // Recursively sanitize nested objects
    Object.keys(sanitized).forEach((key) => {
      if (key !== 'arr') {
        // We already handled arr above
        sanitized[key] = sanitizeDataForFirestore(sanitized[key]);
      }
    });

    return sanitized;
  }

  return data;
}

/**
 * Calculates the size reduction achieved by sanitization
 */
export function calculateSanitizationSizeReduction(originalData: any, sanitizedData: any) {
  // Use browser-compatible method to calculate byte size
  const originalSize = new Blob([
    JSON.stringify(originalData, (key, value) => (TRANSIENT_JSON_FIELDS.has(key) ? undefined : value)),
  ]).size;
  const sanitizedSize = new Blob([JSON.stringify(sanitizedData)]).size;
  const sizeReduction = originalSize - sanitizedSize;
  const reductionPercentage = (sizeReduction / originalSize) * 100;

  return {
    originalSize,
    sanitizedSize,
    sizeReduction,
    reductionPercentage,
    originalSizeKB: originalSize / 1024,
    sanitizedSizeKB: sanitizedSize / 1024,
    reductionKB: sizeReduction / 1024,
    withinFirestoreLimit: sanitizedSize < 1048576,
    percentageOfLimit: (sanitizedSize / 1048576) * 100,
  };
}
